import { GAME_LANGUAGES, isMode, isSort, UUID_PATTERN } from './index.js';
/** Shared between Vite, Worker, Node/Nginx and the browser; no platform imports. */
export function resolvePage(rawPath, rawSearch = '') {
    let path;
    const params = new Map();
    try {
        path = decodeURIComponent(rawPath);
        // Encoded separators, double encoding and control characters are never page routes.
        if (path.includes('%') || /[\\\x00-\x20?#]/.test(path) || /%2f|%5c/i.test(rawPath))
            return { kind: 'error', status: 400 };
        for (const pair of rawSearch.replace(/^\?/, '').split('&').filter(Boolean)) {
            const split = pair.indexOf('=');
            const key = decodeURIComponent((split < 0 ? pair : pair.slice(0, split)).replace(/\+/g, ' '));
            const value = decodeURIComponent((split < 0 ? '' : pair.slice(split + 1)).replace(/\+/g, ' '));
            if (params.has(key) || /[\x00-\x1f\x7f]/.test(key + value))
                return { kind: 'error', status: 400 };
            params.set(key, value);
        }
    }
    catch {
        return { kind: 'error', status: 400 };
    }
    if (!path.startsWith('/') || path.includes('//'))
        return { kind: 'error', status: 404 };
    const clean = path === '/' ? '/' : path.replace(/\/+$/, '');
    let canonical = clean;
    if (clean === '/index.html')
        canonical = '/';
    if (clean === '/game.html')
        canonical = '/play/zh-CN';
    const kept = [];
    const keep = (key) => { if (params.has(key))
        kept.push([key, params.get(key)]); };
    const bad = () => ({ kind: 'error', status: 400 });
    let route;
    if (canonical === '/') {
        if (params.has('debug') && !['0', '1'].includes(params.get('debug')))
            return bad();
        if (params.get('debug') === '1') {
            canonical = '/play/zh-CN';
            route = { kind: 'game', language: 'zh-CN' };
            keep('debug');
        }
        else
            route = { kind: 'home' };
    }
    else if (canonical.startsWith('/play/')) {
        const language = canonical.slice(6);
        if (language === 'zh-CN' && GAME_LANGUAGES[language]) {
            route = { kind: 'game', language };
            if (params.has('debug') && !['0', '1'].includes(params.get('debug')))
                return bad();
            keep('debug');
        }
        else if (language === 'en' || language === 'ja')
            route = { kind: 'language-preview', language };
        else
            return { kind: 'error', status: 404 };
    }
    else if (canonical === '/workshop' || canonical === '/workshop/new') {
        const mode = params.get('mode'), sort = params.get('sort');
        if (mode !== undefined && !isMode(mode))
            return bad();
        if (sort !== undefined && !isSort(sort))
            return bad();
        if (canonical === '/workshop/new')
            route = { kind: 'new', mode: isMode(mode) ? mode : 'izombie' };
        else
            route = { kind: 'workshop', mode: isMode(mode) ? mode : undefined, sort: isSort(sort) ? sort : 'recommended' };
        keep('mode');
        if (canonical === '/workshop')
            keep('sort');
    }
    else if (canonical === '/workshop/import')
        route = { kind: 'import' };
    else if (canonical === '/me/levels')
        route = { kind: 'my-levels' };
    else if (canonical === '/me/favorites')
        route = { kind: 'favorites' };
    else if (canonical === '/help/workshop')
        route = { kind: 'help' };
    else {
        const draft = /^\/workshop\/drafts\/([^/]+)\/(edit|play)$/.exec(canonical);
        const level = /^\/workshop\/levels\/([^/]+)(\/play)?$/.exec(canonical);
        if (draft && UUID_PATTERN.test(draft[1])) {
            route = { kind: 'draft', draftId: draft[1].toLowerCase(), action: draft[2] };
            canonical = '/workshop/drafts/' + route.draftId + '/' + route.action;
        }
        else if (level && UUID_PATTERN.test(level[1])) {
            const version = params.get('v');
            if (version !== undefined && (!/^[1-9][0-9]*$/.test(version) || !Number.isSafeInteger(Number(version))))
                return bad();
            if (level[2] && version === undefined)
                return bad();
            route = { kind: 'level', levelId: level[1].toLowerCase(), revision: version === undefined ? undefined : Number(version), action: level[2] ? 'play' : 'detail' };
            canonical = '/workshop/levels/' + route.levelId + (level[2] ?? '');
            keep('v');
        }
        else
            return { kind: 'error', status: 404 };
    }
    const search = kept.length ? '?' + kept.map(([k, v]) => encodeURIComponent(k) + '=' + encodeURIComponent(v)).join('&') : '';
    if (rawPath + rawSearch !== canonical + search)
        return { kind: 'redirect', location: canonical + search };
    return {
        kind: 'page', route, template: route.kind === 'game' ? 'game.html' : 'index.html',
        noindex: !['home', 'game', 'language-preview', 'help', 'workshop', 'level'].includes(route.kind),
    };
}
/** Public levels require a visibility lookup in T10; never serve unverified detail HTML. */
export function unavailablePublicLevel(decision) {
    return decision.kind === 'page' && decision.route.kind === 'level';
}
/** Standalone error page for the shared router: no external assets, light and dark safe. */
export function pageErrorHTML(status) {
    const message = status === 400 ? '链接参数无效' : status === 405 ? '此页面只接受 GET / HEAD' : '页面不存在或尚未公开';
    const hint = status === 400 ? '地址里的参数不正确。回到首页重新选择入口。'
        : status === 405 ? '这个地址只接受 GET 和 HEAD 请求，请用浏览器正常打开。'
            : '链接可能已失效，或内容还没公开。';
    return [
        '<!doctype html><html lang="zh-CN"><head><meta charset="utf-8">',
        '<meta name="viewport" content="width=device-width,initial-scale=1"><meta name="robots" content="noindex">',
        '<title>', String(status), ' · 植物大战僵尸</title><style>',
        ':root{color-scheme:light;--bg:#f5f5ee;--card:#fff;--fg:#23332a;--muted:#5c6b61;--line:#d7dfd5;--primary:#31573d;--lawn:#e3eccd;--sun:#e0b74d}',
        '@media (prefers-color-scheme:dark){:root{color-scheme:dark;--bg:#101a16;--card:#18251e;--fg:#e9eee7;--muted:#a7b6ab;--line:#34463a;--primary:#b3d29c;--lawn:#293e2b;--sun:#d9b85e}}',
        '*{box-sizing:border-box}body{margin:0;min-height:100svh;display:grid;place-items:center;padding:2rem;background:var(--bg);color:var(--fg);font-family:system-ui,-apple-system,"Segoe UI","Microsoft YaHei",sans-serif}',
        'main{width:min(36rem,100%);background:var(--card);border:1px solid var(--line);border-radius:8px;overflow:hidden}',
        '.band{display:block;height:6px;background:linear-gradient(90deg,var(--sun),var(--lawn))}',
        '.wrap{padding:2rem 1.75rem}',
        '.tag{display:inline-block;margin:0 0 .9rem;padding:3px 10px;border-radius:999px;background:var(--lawn);color:var(--primary);font-size:.75rem;font-weight:600}',
        'h1{margin:0 0 .75rem;font-size:1.55rem;line-height:1.3;letter-spacing:-.02em}',
        '.hint{margin:0 0 1.6rem;color:var(--muted);font-size:.9375rem;line-height:1.75}',
        '.links{display:flex;flex-wrap:wrap;gap:.75rem}',
        'a{display:inline-flex;align-items:center;min-height:40px;padding:0 1rem;border-radius:6px;border:1px solid transparent;text-decoration:none;font-size:.875rem;font-weight:500}',
        '.primary{background:var(--primary);color:var(--bg)}.secondary{border-color:var(--line);color:var(--fg)}',
        'a:focus-visible{outline:2px solid var(--primary);outline-offset:3px}',
        '</style></head><body><main><span class="band"></span><div class="wrap">',
        '<p class="tag">', String(status), '</p><h1>', message, '</h1><p class="hint">', hint, '</p>',
        '<div class="links"><a class="primary" href="/">返回首页</a><a class="secondary" href="/workshop">创意工坊</a></div>',
        '</div></main></body></html>',
    ].join('');
}
